import apiClient from './client';
import type {
  Tender,
  TenderCreatePayload,
  PaginatedResponse,
  TenderStatus,
  WeightedSkill,
} from '../types';

// -------------------------------------------------------------------------
// Query params
// -------------------------------------------------------------------------

export interface TenderListParams {
  page?: number;
  page_size?: number;
  status?: TenderStatus;
  search?: string;
  sort_by?: 'created_at' | 'deadline' | 'title';
  order?: 'asc' | 'desc';
}

// -------------------------------------------------------------------------
// Backend → frontend mapping
// -------------------------------------------------------------------------

/** Parse a JSON-encoded skill list into a string array.
 *  Handles both old format (string[]) and new weighted format ({skill,weight}[]). */
function parseSkills(value: unknown): string[] {
  let arr: unknown[] | null = null;
  if (Array.isArray(value)) {
    arr = value;
  } else if (typeof value === 'string' && value.startsWith('[')) {
    try { arr = JSON.parse(value); } catch { /* fall through */ }
  }
  if (!arr) return [];
  return arr.map((item) => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object' && 'skill' in item) return String((item as { skill: string }).skill);
    return String(item);
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function fixTenderSkills(raw: any): Tender {
  return {
    ...raw,
    required_skills: parseSkills(raw.required_skills ?? raw.requiredSkills),
    nice_to_have_skills: parseSkills(raw.nice_to_have_skills ?? raw.niceToHaveSkills),
    requirements: parseSkills(raw.requirements),
    work_location: raw.work_location ?? raw.workLocation ?? undefined,
    sector: raw.sector ?? undefined,
  };
}

// -------------------------------------------------------------------------
// API functions
// -------------------------------------------------------------------------

/**
 * Fetch a paginated list of tenders.
 * Maps Spring's Page response to the frontend PaginatedResponse shape.
 */
export async function fetchTenders(
  params: TenderListParams = {}
): Promise<PaginatedResponse<Tender>> {
  // Spring Page params: page (0-based), size, sort
  const springParams: Record<string, unknown> = {
    page: (params.page ?? 1) - 1,
    size: params.page_size ?? 20,
    ...(params.search ? { q: params.search } : {}),
    ...(params.status ? { status: params.status } : {}),
    ...(params.sort_by ? { sort: `${params.sort_by.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())},${params.order ?? 'desc'}` } : {}),
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await apiClient.get<any>('/tenders', { params: springParams });

  return {
    items: (data.content ?? []).map(fixTenderSkills),
    total: data.total_elements,
    page: data.number + 1,
    page_size: data.size,
    total_pages: data.total_pages,
  };
}

/**
 * Fetch a single tender by ID.
 */
export async function fetchTender(tenderId: string): Promise<Tender> {
  const { data } = await apiClient.get(`/tenders/${tenderId}`);
  return fixTenderSkills(data);
}

/**
 * Create (submit) a new tender.
 */
export async function createTender(payload: TenderCreatePayload): Promise<Tender> {
  const { data } = await apiClient.post<Tender>('/tenders', payload);
  return data;
}

/**
 * Update an existing tender.
 */
export async function updateTender(
  tenderId: string,
  payload: Partial<TenderCreatePayload>
): Promise<Tender> {
  const { data } = await apiClient.patch<Tender>(`/tenders/${tenderId}`, payload);
  return data;
}

/**
 * Transition a tender to a new status.
 */
export async function updateTenderStatus(
  tenderId: string,
  status: TenderStatus
): Promise<Tender> {
  const { data } = await apiClient.patch<Tender>(`/tenders/${tenderId}/status`, {
    status,
  });
  return data;
}

/**
 * Delete (archive) a tender.
 */
export async function deleteTender(tenderId: string): Promise<void> {
  await apiClient.delete(`/tenders/${tenderId}`);
}

/**
 * Generate (or regenerate) the tender embedding.
 */
export async function embedTender(tenderId: string): Promise<void> {
  await apiClient.post(`/matches/tenders/${tenderId}/embed`);
}

/**
 * Run the matching pipeline for a tender.
 */
export async function triggerMatching(tenderId: string): Promise<void> {
  await apiClient.post(`/matches/tenders/${tenderId}/run`);
}

/**
 * Fetch weighted skills for a tender.
 */
export async function fetchTenderSkills(tenderId: string): Promise<WeightedSkill[]> {
  const { data } = await apiClient.get(`/tenders/${tenderId}/skills`);
  return data;
}

/**
 * Replace all weighted skills for a tender.
 */
export async function updateTenderSkills(tenderId: string, skills: WeightedSkill[]): Promise<WeightedSkill[]> {
  const { data } = await apiClient.put(`/tenders/${tenderId}/skills`, skills);
  return data;
}

/**
 * Add a single weighted skill to a tender.
 */
export async function addTenderSkill(tenderId: string, skill: WeightedSkill): Promise<WeightedSkill[]> {
  const { data } = await apiClient.post(`/tenders/${tenderId}/skills`, skill);
  return data;
}

/**
 * Delete a single skill from a tender.
 */
export async function deleteTenderSkill(tenderId: string, skillName: string): Promise<void> {
  await apiClient.delete(`/tenders/${tenderId}/skills/${encodeURIComponent(skillName)}`);
}

/**
 * Re-extract metadata from a tender's description.
 */
export async function reextractTenderMetadata(tenderId: string): Promise<Tender> {
  const { data } = await apiClient.post(`/tenders/${tenderId}/reextract`);
  return fixTenderSkills(data);
}

/**
 * Upload a tender document (PDF, DOCX, or EML) and create a tender from its content.
 */
export async function uploadTenderFile(
  file: File,
  title?: string,
  onUploadProgress?: (percent: number) => void
): Promise<Tender> {
  const formData = new FormData();
  formData.append('file', file);
  if (title) formData.append('title', title);

  const { data } = await apiClient.post<Tender>('/tenders/upload', formData, {
    headers: { 'Content-Type': undefined },
    onUploadProgress: (event) => {
      if (onUploadProgress && event.total) {
        onUploadProgress(Math.round((event.loaded * 100) / event.total));
      }
    },
  });
  return fixTenderSkills(data);
}
